import { AntDesign } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { router } from "expo-router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollView, View, Pressable } from "react-native";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import Subtitle from "@/components/Subtitle";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import { useAppSelector } from "@/context/hooks";
import { storage } from "@/context/storage";
import { sendRequest } from "@/utils/ApiUtil";
import { CorporateInvestmentProfileObject } from "@/utils/dto/application.request";
import {
  ProfileInvestingUsdValueOfCrypto,
  ProfileInvestingMonthlyCryptoInvestmentDeposit,
  ProfileInvestingMonthlyInvestmentDeposit,
  ProfileInvestingMonthlyCryptoDeposits,
  ProfileInvestingMonthlyDeposits,
  ProfileInvestingMonthlyCryptoInvestmentWithdrawal,
  ProfileInvestingMonthlyInvestmentWithdrawal,
  ProfileInvestingMonthlyCryptoWithdrawals,
  ProfileInvestingMonthlyWithdrawals,
  ProfileInvestingPrimarySourceOfFunds,
} from "@/utils/dto/extracted_enums";

type FormData = {
  primarySourceOfFunds: string;
  primarySourceOfFundsDescription: string;
  usdValueOfCrypto: string;
  usdValueOfFiat: string;
  totalAssets: string;
  monthlyCryptoInvestmentDeposit: string;
  monthlyInvestmentDeposit: string;
  monthlyCryptoDeposits: string;
  monthlyDeposits: string;
  monthlyCryptoInvestmentWithdrawal: string;
  monthlyInvestmentWithdrawal: string;
  monthlyCryptoWithdrawals: string;
  monthlyWithdrawals: string;
};

export default function InvestmentProfile() {
  const { t } = useTranslation();
  const [primarySource, setPrimarySource] = useState("");
  const globalValues = useAppSelector((state: any) => state);

  const resolver = classValidatorResolver(CorporateInvestmentProfileObject, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, formState } = useForm<FormData>({
    mode: "onSubmit",
    resolver,
  });

  const { isSubmitting } = formState;

  const onSubmit = async (data: FormData) => {
    const applicationId = storage.getString("user.auth.applicationId");
    const applicationType = storage.getString("user.auth.applicationType");

    const newObject = Object.entries(data).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key] = value;
      }
      return acc;
    }, {});

    try {
      if (applicationType === "CORPORATION") {
        const corporationCustomerData = globalValues.corporationCustomerData;
        const kycData = globalValues.kycData;
        const descriptionOfBusinessNature =
          globalValues.descriptionOfBusinessNature;
        const object = {
          applicationId,
          customerDetails: {
            ...corporationCustomerData,
            kycProfile: {
              descriptionOfBusinessNature,
              ...kycData,
            },
            investmentProfile: {
              ...newObject,
            },
          },
        };
        const request = await sendRequest(
          "post",
          "/customer-details-corporation",
          object,
          true
        );
        if (request) {
          router.replace("/corporation/registeredaddress");
        }
      } else if (applicationType === "INDIVIDUAL") {
        const individualCustomerData = globalValues.individualCustomerData;
        const kycData = globalValues.kycData;

        const object = {
          applicationId,
          customerDetails: {
            ...individualCustomerData,
            kycProfile: {
              ...kycData,
            },
            investmentProfile: {
              ...newObject,
            },
          },
        };

        const request = await sendRequest(
          "post",
          "/customer-details-individual",
          object,
          true
        );
        if (request) {
          router.replace("/individual/address");
        }
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  return (
    <ScrollView contentContainerClassName="flex-grow items-center bg-white1">
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      <Logo className="m-4" />
      <View className="m-1">
        <Title text={t("onboarding/investmentprofile:title")} />
      </View>
      <View className="m-1">
        <Subtitle text={t("onboarding/investmentprofile:subtitle")} />
      </View>
      <View className="z-50 items-center p-6 lg:border lg:border-solid lg:border-gray lg:shadow lg:rounded-md lg:w-3/5">
        <Pressable
          onPress={() => {
            router.push("/kyc");
          }}
          className="absolute z-[140] top-0 left-4 lg:left-6 lg:top-10"
        >
          <AntDesign name="arrowleft" size={24} color="#BDBDBD" />
        </Pressable>
        <View className="z-[130] max-w-80">
          <InputChoose
            control={control}
            name="primarySourceOfFunds"
            label={t("onboarding/investmentprofile:input.primarysourceoffunds")}
            handleChange={(value: string) => setPrimarySource(value)}
            data={{ ProfileInvestingPrimarySourceOfFunds }}
          />
        </View>
        {primarySource === "OTHER" && (
          <View className="z-[120]">
            <Input
              control={control}
              name="primarySourceOfFundsDescription"
              label={t(
                "onboarding/investmentprofile:input.primarysourceoffundsdescription"
              )}
            />
          </View>
        )}
        <View className="z-[110] max-w-80">
          <InputChoose
            control={control}
            name="usdValueOfFiat"
            label={t("onboarding/investmentprofile:input.usdvalueoffiat")}
            data={{ ProfileInvestingUsdValueOfCrypto }}
          />
        </View>
        <View className="z-[100] max-w-80">
          <InputChoose
            control={control}
            name="usdValueOfCrypto"
            label={t("onboarding/investmentprofile:input.usdvalueofcrypto")}
            data={{ ProfileInvestingUsdValueOfCrypto }}
          />
        </View>
        <View className="z-[90] max-w-80">
          <InputChoose
            control={control}
            name="totalAssets"
            label={t("onboarding/investmentprofile:input.totalassets")}
            data={{ ProfileInvestingUsdValueOfCrypto }}
          />
        </View>
        <View className="z-[80] max-w-80">
          <InputChoose
            control={control}
            name="monthlyCryptoInvestmentDeposit"
            label={t(
              "onboarding/investmentprofile:input.monthlycryptoinvestmentdeposit"
            )}
            data={{ ProfileInvestingMonthlyCryptoInvestmentDeposit }}
          />
        </View>
        <View className="z-[70] max-w-80">
          <InputChoose
            control={control}
            name="monthlyInvestmentDeposit"
            label={t(
              "onboarding/investmentprofile:input.monthlyinvestmentdeposit"
            )}
            data={{ ProfileInvestingMonthlyInvestmentDeposit }}
          />
        </View>
        <View className="z-[60] max-w-80">
          <InputChoose
            control={control}
            name="monthlyCryptoDeposits"
            label={t(
              "onboarding/investmentprofile:input.monthlycryptodeposits"
            )}
            data={{ ProfileInvestingMonthlyCryptoDeposits }}
          />
        </View>
        <View className="z-50 max-w-80">
          <InputChoose
            control={control}
            name="monthlyDeposits"
            label={t("onboarding/investmentprofile:input.monthlydeposits")}
            data={{ ProfileInvestingMonthlyDeposits }}
          />
        </View>
        <View className="z-40 max-w-80">
          <InputChoose
            control={control}
            name="monthlyCryptoInvestmentWithdrawal"
            label={t(
              "onboarding/investmentprofile:input.monthlycryptoinvestmentwithdrawal"
            )}
            data={{ ProfileInvestingMonthlyCryptoInvestmentWithdrawal }}
          />
        </View>
        <View className="z-30 max-w-80">
          <InputChoose
            control={control}
            name="monthlyInvestmentWithdrawal"
            label={t(
              "onboarding/investmentprofile:input.monthlyinvestmentwithdrawal"
            )}
            data={{ ProfileInvestingMonthlyInvestmentWithdrawal }}
          />
        </View>
        <View className="z-20 max-w-80">
          <InputChoose
            control={control}
            name="monthlyCryptoWithdrawals"
            label={t(
              "onboarding/investmentprofile:input.monthlycryptowithdrawals"
            )}
            data={{ ProfileInvestingMonthlyCryptoWithdrawals }}
          />
        </View>
        <View className="z-10 max-w-80">
          <InputChoose
            control={control}
            name="monthlyWithdrawals"
            label={t("onboarding/investmentprofile:input.monthlywithdrawals")}
            data={{ ProfileInvestingMonthlyWithdrawals }}
          />
        </View>
      </View>
      <Button
        label={t("common:button.nextstep")}
        onClick={handleSubmit(onSubmit)}
      />
      <Copyright />
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
